import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { Condition, ConditionCategory, ConditionStatus, CATEGORIES } from '../types/condition';
type AddConditionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (condition: Omit<Condition, 'id'>) => void;
  defaultStatus?: ConditionStatus;
};
export function AddConditionModal({
  isOpen,
  onClose,
  onAdd,
  defaultStatus = 'Pending'
}: AddConditionModalProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<ConditionCategory>('Income');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !dueDate || !assignee.trim()) return;
    onAdd({
      title: title.trim(),
      category,
      status: defaultStatus,
      dueDate,
      assignee: assignee.trim(),
      notes: notes.trim(),
      priority
    });
    // Reset form
    setTitle('');
    setCategory('Income');
    setDueDate('');
    setAssignee('');
    setNotes('');
    setPriority('medium');
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-2">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-3" />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" data-id="element-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-5">
          <h2 id="modal-title" className="text-lg font-semibold text-gray-900" data-id="element-6">
            Add New Condition
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label="Close modal" data-id="element-7">
            <XIcon className="w-5 h-5" data-id="element-8" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-id="element-9">
          {/* Title */}
          <div data-id="element-10">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11">
              Condition Title <span className="text-red-500" data-id="element-12">*</span>
            </label>
            <input id="title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., W-2 Forms 2023-2024" className="
                w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                placeholder:text-gray-400
              " required data-id="element-13" />
          </div>

          {/* Category + Priority Row */}
          <div className="grid grid-cols-2 gap-4" data-id="element-14">
            <div data-id="element-15">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-16">
                Category
              </label>
              <select id="category" value={category} onChange={e => setCategory(e.target.value as ConditionCategory)} className="
                  w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  bg-white
                " data-id="element-17">
                {CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-18">
                    {cat}
                  </option>)}
              </select>
            </div>

            <div data-id="element-19">
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20">
                Priority
              </label>
              <select id="priority" value={priority} onChange={e => setPriority(e.target.value as 'high' | 'medium' | 'low')} className="
                  w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  bg-white
                " data-id="element-21">
                <option value="high" data-id="element-22">High</option>
                <option value="medium" data-id="element-23">Medium</option>
                <option value="low" data-id="element-24">Low</option>
              </select>
            </div>
          </div>

          {/* Due Date + Assignee Row */}
          <div className="grid grid-cols-2 gap-4" data-id="element-25">
            <div data-id="element-26">
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-27">
                Due Date <span className="text-red-500" data-id="element-28">*</span>
              </label>
              <input id="dueDate" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="
                  w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                " required data-id="element-29" />
            </div>

            <div data-id="element-30">
              <label htmlFor="assignee" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-31">
                Assignee <span className="text-red-500" data-id="element-32">*</span>
              </label>
              <input id="assignee" type="text" value={assignee} onChange={e => setAssignee(e.target.value)} placeholder="e.g., John" className="
                  w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  placeholder:text-gray-400
                " required data-id="element-33" />
            </div>
          </div>

          {/* Notes */}
          <div data-id="element-34">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-35">
              Notes
            </label>
            <textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional details or instructions..." rows={3} className="
                w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                placeholder:text-gray-400
              " data-id="element-36" />
          </div>

          {/* Status Info */}
          <div className="bg-gray-50 rounded-lg px-4 py-3" data-id="element-37">
            <p className="text-sm text-gray-600" data-id="element-38">
              This condition will be added to{' '}
              <span className="font-medium text-gray-900" data-id="element-39">{defaultStatus}</span>
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2" data-id="element-40">
            <button type="button" onClick={onClose} className="
                px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg
                hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              " data-id="element-41">
              Cancel
            </button>
            <button type="submit" className="
                px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg
                hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed
              " disabled={!title.trim() || !dueDate || !assignee.trim()} data-id="element-42">
              Add Condition
            </button>
          </div>
        </form>
      </div>
    </div>;
}